import {
  Flex,
  IconButton,
  Link,
  Spinner,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Input, Switch } from '@gamma/ui/form-fields';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import _ from 'lodash';
import {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ExporterTLSInfoInput,
  SplunkHecLogsExporter,
  useSaveExporter,
} from '../../queries';

import { Controller, useForm, ControllerRenderProps } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { URLS } from '../../../../shared/constants/constants';
import { ExporterType } from '../../../../shared/generated/graphql';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers';

import { InfoIcon } from '@chakra-ui/icons';
import { validateExporterUrl } from '../../../../shared/utils/validateExporterUrl';
import { useSetExporterFormState } from '../../hooks/useSetExporterFormState';
import { EXPORTER_DEFAULTS } from '../../pages/IntegrationsConstants';
import { ExporterSslSwitch, OnChangeSwitchProps } from '../ExporterSslSwitch';

const { SPLUNK_DOCS_ENABLE_HTTP_EVENT_COLLECTOR } = URLS;

interface SplunkHECFormProps {
  isUpdating: boolean;
  deleteLoading: boolean;
  onDeleteOpen: () => void;
  clearInProgress: () => void;
  activeExporter: SplunkHecLogsExporter | null;
  setIsUpdating: (isUpdating: boolean) => void;
  setIsSaveLoading: (isSaveLoading: boolean) => void;
  setIsSaveDisabled: (isSaveDisabled: boolean) => void;
  setIsFormUnchanged: (isFormUnchanged: boolean) => void;
  setIsCancelDisabled: (isCancelDisabled: boolean) => void;
  setFormRef: (formRef: RefObject<HTMLFormElement> | null) => void;
  refetchExporters: () => Promise<unknown>;
}

interface AlertExportData {
  name: string;
  enabled: boolean;
  hec_url: string;
  token: string;
  index: string;
  tls: ExporterTLSInfoInput;
}

export const SplunkHECForm = ({
  isUpdating,
  setFormRef,
  onDeleteOpen,
  deleteLoading,
  setIsUpdating,
  activeExporter,
  clearInProgress,
  setIsSaveLoading,
  setIsSaveDisabled,
  setIsFormUnchanged,
  setIsCancelDisabled,
  refetchExporters,
}: SplunkHECFormProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<AlertExportData>({
    mode: 'onChange',
    defaultValues: {
      ...EXPORTER_DEFAULTS,
      hec_url: '',
      token: '',
      index: '',
    },
  });

  const { enabled: enabledLabel, disabled: disabledLabel } =
    i18n.pages.integrations;

  const { forms, splunkHECForm, toast } = i18n.pages.alertExport;

  const { splunkInstructions, splunkDocumentation, splunkHECUrlFormatTooltip } =
    splunkHECForm;

  const { saveSuccessDesc, saveSuccessTitle, saveFailDesc, saveFailTitle } =
    toast;

  const navigate = useNavigate();
  const showToastMessage = useToast();
  const { user } = useContext(AuthContext);
  const tenant = user?.attributes?.['custom:tenant_id'];

  const formData = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const [exportConfig, setExportConfig] = useState<
    AlertExportData | SplunkHecLogsExporter | null
  >(activeExporter);
  const [shouldDisableSave, setShouldDisableSave] = useState(true);

  const { name, enabled, hec_url, token, tls, index } = exportConfig || {};

  const fillForm = useCallback(() => {
    setValue('name', name || '');
    setValue('enabled', enabled || false);
    setValue('hec_url', hec_url || '');
    setValue('token', token || '');
    setValue('index', index || '');
    setValue('tls', tls || EXPORTER_DEFAULTS.tls);
  }, [setValue, name, enabled, hec_url, token, index, tls]);

  useEffect(() => {
    setExportConfig(activeExporter);
  }, [activeExporter]);

  useEffect(() => {
    fillForm();
  }, [activeExporter, exportConfig, fillForm]);

  const [saveExporter, { loading: saveLoading, error: _saveError }] =
    useSaveExporter({
      onCompleted: async (data) => {
        const { saveExporter: savedExporter } = data;
        const exporter_id = savedExporter?.exporter_id;

        // Refetch triggers cache eviction and data refresh in parent
        await refetchExporters();

        setShouldDisableSave(true);
        setIsUpdating(false);

        showToastMessage({
          status: 'success',
          title: saveSuccessTitle,
          description: saveSuccessDesc,
          isClosable: true,
          position: 'bottom-right',
        });
        clearInProgress();
        if (exporter_id) {
          navigate(exporter_id);
        }
      },
      onError: () => {
        setShouldDisableSave(false);
        setIsUpdating(false);
        showToastMessage({
          status: 'error',
          title: saveFailTitle,
          description: saveFailDesc,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const formIsPopulated = Object.values(formData).every(
    (value) => value !== '',
  );

  const formIsUnchanged = _.isEqual(formData, {
    name,
    enabled,
    hec_url,
    token,
    index,
    tls,
  });

  const hasErrors = Object.keys(errors).length > 0;
  const isSaveDisabled =
    formIsUnchanged || !formIsPopulated || saveLoading || hasErrors;

  // only need to do this once and update the state else where like after a save or delete network request.
  useEffect(() => {
    setShouldDisableSave(isSaveDisabled);
  }, []);

  const submitAlertExport = async (data: AlertExportData) => {
    setShouldDisableSave(false);
    setIsUpdating(true);
    setExportConfig(data);
    const { name, enabled, hec_url, token, index, tls } = data;
    const input = {
      tenant: tenant || '',
      name,
      enabled,
      exporter: ExporterType.SplunkHecLogs,
      splunk_hec_logs: {
        hec_url,
        token,
        index,
        tls: {
          verify_certificate: tls.verify_certificate,
        },
      },
      exporter_id: activeExporter?.exporter_id || null,
    };
    saveExporter({
      variables: {
        input,
      },
    });
  };

  useSetExporterFormState({
    formRef,
    isUpdating,
    saveLoading,
    deleteLoading,
    isSaveDisabled,
    formIsUnchanged,
    shouldDisableSave,
    setFormRef,
    setIsSaveLoading,
    setIsSaveDisabled,
    setIsFormUnchanged,
    setIsCancelDisabled,
  });

  return (
    <>
      <VStack my={2} spacing={4} alignItems="start">
        <Text textStyle="body-md">
          {splunkInstructions}{' '}
          <Link
            isExternal
            color="blue.400"
            textDecoration="none"
            href={SPLUNK_DOCS_ENABLE_HTTP_EVENT_COLLECTOR}
          >
            {splunkDocumentation}
          </Link>
          .
        </Text>
      </VStack>
      <VStack
        as="form"
        spacing={4}
        ref={formRef}
        align="flex-start"
        id="alertExportForm"
        onSubmit={handleSubmit(submitAlertExport)}
      >
        <Controller
          name="enabled"
          control={control}
          render={({
            field: { onChange, name, value },
          }: {
            field: ControllerRenderProps<AlertExportData, 'enabled'>;
          }) => (
            <Switch
              inline
              name={name}
              isChecked={value}
              onChange={onChange}
              data-testid="exportSwitch"
              isDisabled={isUpdating}
              label={formData.enabled ? enabledLabel : disabledLabel}
            />
          )}
        />
        <Flex w="100%">
          <Input
            {...register('name', { required: true })}
            isRequired
            label={forms.exporterName}
            isDisabled={isUpdating}
          />
        </Flex>
        <VStack w="100%" spacing={1} align="stretch">
          {errors.hec_url && (
            <Text color="red.500" fontSize="sm">
              {errors.hec_url.message || 'Invalid URL format'}
            </Text>
          )}

          <Input
            {...register('hec_url', {
              required: true,
              validate: {
                notCollectorSuffix: (value) =>
                  !value.endsWith('/services/collector') ||
                  'Please remove the suffix "/services/collector".',
                validHEC: (value) => {
                  return validateExporterUrl(value);
                },
              },
            })}
            isRequired
            label={forms.url}
            isDisabled={isUpdating}
            rightElement={
              <Tooltip
                label={splunkHECUrlFormatTooltip}
                placement="top"
                hasArrow
                shouldWrapChildren
              >
                <Link
                  href={URLS.SPLUNK_HEC_URL_FORMAT}
                  isExternal
                  display="flex"
                  alignItems="center"
                  color="blue.400"
                  mr={2}
                >
                  <InfoIcon boxSize={4} />
                </Link>
              </Tooltip>
            }
          />
        </VStack>
        <Flex w="100%">
          <Input
            {...register('token', { required: true })}
            isRequired
            label={forms.token}
            isDisabled={isUpdating}
          />
        </Flex>
        <Flex w="100%">
          <Input
            {...register('index', { required: true })}
            isRequired
            label={forms.index}
            isDisabled={isUpdating}
          />
        </Flex>
        <Flex w="100%" alignItems="end" justifyContent="space-between">
          <Controller
            name="tls"
            control={control}
            render={({
              field: { onChange, name, value },
            }: {
              field: ControllerRenderProps<AlertExportData, 'tls'>;
            }) => (
              <ExporterSslSwitch
                name={name}
                value={value}
                isUpdating={isUpdating}
                onChange={(props: OnChangeSwitchProps) => {
                  onChange({
                    ...tls,
                    verify_certificate: props.verify_certificate,
                  });
                  setValue('tls', {
                    ...tls,
                    verify_certificate: props.verify_certificate,
                  });
                }}
              />
            )}
          />
          {!activeExporter?.inProgress && (
            <IconButton
              size="sm"
              variant="solid"
              colorScheme="red"
              aria-label={forms.deleteBtn}
              isDisabled={saveLoading || deleteLoading}
              data-testid="alert-exporter-delete-button"
              onClick={() => {
                setIsUpdating(true);
                onDeleteOpen();
              }}
              icon={
                deleteLoading ? (
                  <Spinner size={'xs'} />
                ) : (
                  <DeleteSharpIcon boxSize={5} />
                )
              }
            />
          )}
        </Flex>
      </VStack>
    </>
  );
};
